import type {
  ILoanContractNumberOptionMeta,
  ILoanTrancheDisbursementApplicationForm,
  ILoanTrancheDisbursementApplicationRequest,
} from "@/modules/application/common/types";
import {
  useCreateLoanTrancheDisbursementApplicationMutation,
  useGetLoanTrancheDisbursementApplicationQuery,
  useUpdateLoanTrancheDisbursementApplicationMutation
} from "@/modules/application/services";
import { useGetLoansQuery } from "@/modules/loan/services";
import { CURRENCY, INPUT_MAX_LIMIT } from "@/shared/common/constants";
import { Currencies } from "@/shared/common/enums";
import type { IExtendedObject, IOperationPageProps } from "@/shared/common/types";
import { Select } from "@/shared/components";
import { ApplicationLayout } from "@/shared/components/ApplicationLayout";
import { cleanupAmount, isValidAmount } from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import uuid4 from "uuid4";

import { LoanContractNumberSelectOption } from "./LoanContractNumberSelectOption";

export const LoanTrancheDisbursementApplicationForm = ({ isEdit, isCopy }: IOperationPageProps) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const idempotencyKey = useRef(uuid4()).current;
  const { errors = {}, reset, watch } = useFormProps();

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});
  const [selectedLoanCurrency, setSelectedLoanCurrency] = useState<Currencies>(Currencies.AMD);

  const {
    data: loans,
  } = useGetLoansQuery({
    pageSize: 1000,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: loanTrancheDisbursementApplication,
    isLoading: isGetApplicationLoading,
  } = useGetLoanTrancheDisbursementApplicationQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const [
    createLoanTrancheDisbursementApplicationApi,
    { isLoading: isCreateLoading }
  ] = useCreateLoanTrancheDisbursementApplicationMutation();

  const [
    updateLoanTrancheDisbursementApplicationApi,
    { isLoading: isUpdateLoading }
  ] = useUpdateLoanTrancheDisbursementApplicationMutation();

  const loanTrancheDisbursementApplicationAction = useCallback((values: ILoanTrancheDisbursementApplicationForm) => {
    const requestData: ILoanTrancheDisbursementApplicationRequest = {
      ...values,
      trancheAmount: +cleanupAmount(`${values.trancheAmount}`),
    };
    return isEdit ? updateLoanTrancheDisbursementApplicationApi({
        data: requestData,
        id: `${loanTrancheDisbursementApplication?.id}`,
      }).unwrap() :
      createLoanTrancheDisbursementApplicationApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [loanTrancheDisbursementApplication]);

  const loanNumberOptions = useMemo(() => {
    if (!loans?.length) return [];
    return loans?.map((loan, index) => ({
      value: loan.contractNumber,
      label: loan.contractNumber,
      meta: {
        currency: loan.currency,
        type: loan.loanType,
        index,
      },
    }));
  }, [loans]);

  const values = watch?.() || {};

  const setDefaultValues = () => {
    const data = {
      loanContractNumber: loanTrancheDisbursementApplication?.loanContractNumber,
      trancheAmount: loanTrancheDisbursementApplication?.trancheAmount,
      purpose: loanTrancheDisbursementApplication?.purpose,
      applicationNumber: isEdit ?
        loanTrancheDisbursementApplication?.applicationNumber : values.applicationNumber,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    purpose: values.purpose,
    trancheAmount: values.trancheAmount,
    loanContractNumber: values.loanContractNumber,
    applicationNumber: values.applicationNumber,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!loanTrancheDisbursementApplication?.id) {
      setDefaultValues();
    }
  }, [loanTrancheDisbursementApplication]);

  return (
    <ApplicationLayout
      title={t("application.loanTrancheDisbursement.title")}
      page="Loan tranche disbursement application"
      isEdit={isEdit}
      hasChanges={hasChanges}
      isLoading={isCreateLoading || isUpdateLoading}
      isGetApplicationLoading={isGetApplicationLoading}
      getApplicationId={loanTrancheDisbursementApplicationAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="loanContractNumber"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    className="custom-select"
                    options={loanNumberOptions}
                    label={t("application.loanTrancheDisbursement.loanNumber")}
                    placeholder={t("application.loanTrancheDisbursement.chooseLoanNumber")}
                    handleChange={(option) => {
                      const { meta } = option as TOption;
                      setSelectedLoanCurrency((meta as ILoanContractNumberOptionMeta)?.currency as Currencies);
                    }}
                    components={{ Option: LoanContractNumberSelectOption }}
                  />
                )}
              />
              <FormField
                name="trancheAmount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    label={t("application.loanTrancheDisbursement.trancheAmount")}
                    placeholder={t("application.loanTrancheDisbursement.enterTrancheAmount")}
                    thousandSeparator=","
                    decimalScale={selectedLoanCurrency === CURRENCY.AMD.label ? 1 : 2}
                    rightIconProps={{
                      Component: CURRENCY[selectedLoanCurrency as Currencies]?.symbolIcon || "",
                    }}
                    isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-8"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
            </>,
            title: t("application.applicationDetails"),
            value: "application_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="applicationNumber"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.applicationNumber")}
                    placeholder={t("application.enterApplicationNumber")}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </ApplicationLayout>
  );
};
